import React, { useRef, useState, useEffect } from 'react';
import { AlertCircle, X, Plus } from 'lucide-react';
import './CourseCreationTabs.css';

// ============================================================================
// Input Component
// ============================================================================
export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  required?: boolean;
  icon?: React.ComponentType<{ size?: number; className?: string }>;
}

export const Input: React.FC<InputProps> = ({
  label,
  error,
  required,
  icon: Icon,
  className = '',
  ...props
}) => {
  return (
    <div className="form-group">
      {label && (
        <label className="form-label">
          {label} {required && <span className="required">*</span>}
        </label>
      )}
      <div className="input-wrapper">
        {Icon && <Icon className="input-icon" size={20} />}
        <input
          className={`form-input ${Icon ? 'with-icon' : ''} ${error ? 'error' : ''} ${className}`}
          {...props}
        />
      </div>
      {error && (
        <div className="error-message">
          <AlertCircle size={16} />
          {error}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Select Component
// ============================================================================
export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
}

export const Select: React.FC<SelectProps> = ({
  label,
  error,
  required,
  children,
  className = '',
  ...props
}) => {
  return (
    <div className="form-group">
      {label && (
        <label className="form-label">
          {label} {required && <span className="required">*</span>}
        </label>
      )}
      <select
        className={`form-select ${error ? 'error' : ''} ${className}`}
        {...props}
      >
        {children}
      </select>
      {error && (
        <div className="error-message">
          <AlertCircle size={16} />
          {error}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Textarea Component
// ============================================================================
export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  required?: boolean;
  showCharCount?: boolean;
}

export const Textarea: React.FC<TextareaProps> = ({
  label,
  error,
  required,
  className = '',
  showCharCount,
  maxLength,
  value,
  ...props
}) => {
  return (
    <div className="form-group">
      {label && (
        <label className="form-label">
          {label} {required && <span className="required">*</span>}
        </label>
      )}
      <textarea
        className={`form-textarea ${error ? 'error' : ''} ${className}`}
        maxLength={maxLength}
        value={value}
        {...props}
      />
      <div className="textarea-footer">
        {error && (
          <div className="error-message">
            <AlertCircle size={16} />
            {error}
          </div>
        )}
        {showCharCount && maxLength && (
          <span className="char-count">
            {String(value || '').length}/{maxLength}
          </span>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Button Component
// ============================================================================
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'ghost';
  size?: 'small' | 'medium' | 'large';
  loading?: boolean;
  icon?: React.ComponentType<{ size?: number }>;
  children?: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  loading = false,
  disabled = false,
  icon: Icon,
  children,
  className = '',
  ...props
}) => {
  return (
    <button
      className={`btn btn-${variant} btn-${size} ${className}`}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <div className="spinner" />
      ) : Icon ? (
        <Icon size={size === 'small' ? 16 : 20} />
      ) : null}
      {children}
    </button>
  );
};

// ============================================================================
// Array Field Component
// ============================================================================
export interface ArrayFieldProps {
  label?: string;
  items: any[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  renderItem: (item: any, index: number) => React.ReactNode;
  addButtonText?: string;
  icon?: React.ComponentType<{ size?: number }>;
  className?: string;
}

export const ArrayField: React.FC<ArrayFieldProps> = ({
  label,
  items,
  onAdd,
  onRemove,
  renderItem,
  addButtonText = 'Add Item',
  icon: Icon,
  className = '',
}) => {
  return (
    <div className={`array-field ${className}`}>
      {label && <label className="form-label">{label}</label>}
      <div className="array-items">
        {items.map((item, index) => (
          <div key={index} className="array-item">
            {renderItem(item, index)}
            {items.length > 1 && (
              <Button
                variant="ghost"
                size="small"
                icon={X}
                onClick={() => onRemove(index)}
                className="remove-btn"
              />
            )}
          </div>
        ))}
        <Button
          variant="ghost"
          size="medium"
          icon={Icon || Plus}
          onClick={onAdd}
          className="add-btn"
        >
          {addButtonText}
        </Button>
      </div>
    </div>
  );
};

// ============================================================================
// Keyword Tags Component
// ============================================================================
export interface KeywordTagsProps {
  keywords: string[];
  onAdd: (keyword: string) => void;
  onRemove: (index: number) => void;
  error?: string;
  placeholder?: string;
}

export const KeywordTags: React.FC<KeywordTagsProps> = ({
  keywords,
  onAdd,
  onRemove,
  error,
  placeholder = 'Add a keyword',
}) => {
  const [inputValue, setInputValue] = useState('');

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const keyword = inputValue.trim();
      if (keyword && !keywords.includes(keyword)) {
        onAdd(keyword);
        setInputValue('');
      }
    }
  };

  const handleAddClick = () => {
    const keyword = inputValue.trim();
    if (keyword && !keywords.includes(keyword)) {
      onAdd(keyword);
      setInputValue('');
    }
  };

  return (
    <div className="keyword-tags">
      <div className={`keywords-display ${error ? 'error' : ''}`}>
        {keywords.map((keyword, index) => (
          <span key={index} className="keyword-tag">
            {keyword}
            <button
              type="button"
              onClick={() => onRemove(index)}
              className="keyword-remove"
            >
              <X size={12} />
            </button>
          </span>
        ))}
        {keywords.length === 0 && (
          <span className="keywords-placeholder">No keywords added</span>
        )}
      </div>
      <div className="keywords-input">
        <input
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          className="form-input"
        />
        <Button variant="primary" size="medium" onClick={handleAddClick}>
          Add
        </Button>
      </div>
      {error && (
        <div className="error-message">
          <AlertCircle size={16} />
          {error}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Upload Area Component
// ============================================================================
export interface UploadAreaProps {
  onUpload: (file: File) => void;
  onRemove: () => void;
  file: File | string | null;
  existingFile?: string | null;
  error?: string;
  accept?: string;
  maxSize?: string;
  icon?: React.ComponentType<{ size?: number }>;
  title?: string;
  description?: string;
}

export const UploadArea: React.FC<UploadAreaProps> = ({
  onUpload,
  onRemove,
  file,
  existingFile,
  error,
  accept = 'image/*',
  maxSize = '2MB',
  icon: Icon,
  title = 'Click to upload or drag and drop',
  description,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    if (file && typeof file !== 'string' && file.type?.startsWith('image/')) {
      const objectUrl = URL.createObjectURL(file);
      setPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else if (file && typeof file === 'string' && accept.includes('image')) {
      setPreview(file);
    } else if (existingFile && accept.includes('image')) {
      setPreview(existingFile);
    } else {
      setPreview(null);
    }
  }, [file, existingFile, accept]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      onUpload(selectedFile);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      onUpload(droppedFile);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div
      className={`upload-area ${error ? 'error' : ''}`}
      onDrop={handleDrop}
      onDragOver={(e) => e.preventDefault()}
    >
      {(file || existingFile) ? (
        <div className="upload-success">
          <div className="upload-icon">
            {preview ? (
              <img
                src={preview}
                alt="Preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '80px',
                  borderRadius: '8px',
                  objectFit: 'cover',
                }}
              />
            ) : (
              Icon && <Icon size={32} />
            )}
          </div>
          <p>
            {file
              ? typeof file === 'string'
                ? file.includes('/')
                  ? file.split('/').pop()
                  : file
                : file.name
              : existingFile
              ? existingFile.includes('/')
                ? existingFile.split('/').pop()
                : existingFile
              : 'File uploaded'}
          </p>
          <Button variant="danger" size="small" onClick={onRemove}>
            Remove
          </Button>
        </div>
      ) : (
        <div className="upload-placeholder" onClick={handleClick}>
          {Icon && <Icon size={48} />}
          <div className="upload-text">
            <p>{title}</p>
            {description && <p className="upload-description">{description}</p>}
          </div>
          <Button variant="primary" size="medium">
            Upload File
          </Button>
          <input
            type="file"
            accept={accept}
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>
      )}
      {error && (
        <div className="error-message">
          <AlertCircle size={16} />
          {error}
        </div>
      )}
    </div>
  );
};
